import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingCartAction from "@/components/FloatingCartAction";
import { api } from "@/lib/api";
import { buildLoginPath } from "@/lib/loginRedirect";
import { decimalInputProps, decimalOnly, digitsOnly, integerInputProps, integerOnly, pincodeInputProps } from "@/lib/numberInput";
import { getBrandsForProduct } from "@/lib/productOptions";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, ShoppingCart } from "lucide-react";

const CEMENT_ROWS = [
  { id: "cement-43", brand: "zuori", grade: "43 Grade", location: "Mumbai", price: 5000, price_mt: 5000, unit: "MT", quantity: "26" },
  { id: "cement-53", brand: "zuori", grade: "53 Grade", location: "Mumbai", price: 5000, price_mt: 5000, unit: "MT", quantity: "26" },
];

const CEMENT_QTY_OPTIONS = {
  bag: ["520", "600", "720"],
  MT: ["26", "30", "38"],
};

const DI_PIPES_ROWS = [
  { id: "di-80", size: "80mm", brand: "Electrosteel", location: "Mumbai", k7_price: 1800, k9_price: 2200, quantity: 1 },
  { id: "di-100", size: "100mm", brand: "Electrosteel", location: "Mumbai", k7_price: 2200, k9_price: 2600, quantity: 1 },
  { id: "di-150", size: "150mm", brand: "Electrosteel", location: "Mumbai", k7_price: 3000, k9_price: 3500, quantity: 1 },
  { id: "di-200", size: "200mm", brand: "Electrosteel", location: "Mumbai", k7_price: 3800, k9_price: 4500, quantity: 1 },
  { id: "di-250", size: "250mm", brand: "Electrosteel", location: "Mumbai", k7_price: 4700, k9_price: 5500, quantity: 1 },
  { id: "di-300", size: "300mm", brand: "Electrosteel", location: "Mumbai", k7_price: 5600, k9_price: 6600, quantity: 1 },
  { id: "di-350", size: "350mm", brand: "Electrosteel", location: "Mumbai", k7_price: 6600, k9_price: 7700, quantity: 1 },
  { id: "di-400", size: "400mm", brand: "Electrosteel", location: "Mumbai", k7_price: 7600, k9_price: 8900, quantity: 1 },
  { id: "di-500", size: "500mm", brand: "Electrosteel", location: "Mumbai", k7_price: 9800, k9_price: 11500, quantity: 1 },
  { id: "di-600", size: "600mm", brand: "Electrosteel", location: "Mumbai", k7_price: 12100, k9_price: 14100, quantity: 1 },
  { id: "di-700", size: "700mm", brand: "Electrosteel", location: "Mumbai", k7_price: 14600, k9_price: 17100, quantity: 1 },
  { id: "di-800", size: "800mm", brand: "Electrosteel", location: "Mumbai", k7_price: 17300, k9_price: 20200, quantity: 1 },
  { id: "di-900", size: "900mm", brand: "Electrosteel", location: "Mumbai", k7_price: 20100, k9_price: 23500, quantity: 1 },
  { id: "di-1000", size: "1000mm", brand: "Electrosteel", location: "Mumbai", k7_price: 23000, k9_price: 26900, quantity: 1 },
  { id: "di-1100", size: "1100mm", brand: "Electrosteel", location: "Mumbai", k7_price: 26100, k9_price: 30500, quantity: 1 },
  { id: "di-1200", size: "1200mm", brand: "Electrosteel", location: "Mumbai", k7_price: 29300, k9_price: 34300, quantity: 1 },
];

const ACC_BLOCK_ROWS = [
  { id: "acc-4", size: "4 inch", brand: "Magicrete", location: "Mumbai", price_per_m3: 4000, quantity: 1 },
  { id: "acc-6", size: "6 inch", brand: "Magicrete", location: "Mumbai", price_per_m3: 4000, quantity: 1 },
  { id: "acc-8", size: "8 inch", brand: "Magicrete", location: "Mumbai", price_per_m3: 4000, quantity: 1 },
];
const ACC_BLOCK_BRANDS = ["Ultra Tech", "Infra Market"];
const STEEL_CONTRACTOR_PIECES_PER_TONNE = {
  "8mm": 211,
  "10mm": 135,
  "12mm": 94,
  "16mm": 52,
  "20mm": 33,
  "25mm": 21,
  "32mm": 13,
};

function buildSteelContractorRows() {
  return Object.entries(STEEL_CONTRACTOR_PIECES_PER_TONNE).map(([size, pieces_per_tonne]) => ({
    id: `steel-contractor-${size}`,
    size,
    pieces_per_tonne,
    pieces_input: "",
    tonnes_input: "",
  }));
}

function trimNumericString(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "";
  return String(parseFloat(num.toFixed(3)));
}

function buildCementContractorRows() {
  return CEMENT_ROWS.map((r) => ({
    id: `${r.id}-contractor`,
    grade: r.grade,
    bag_input: "",
    mt_input: "",
    input_mode: "MT",
  }));
}

function buildDiContractorRows() {
  return DI_PIPES_ROWS.map((r) => ({
    id: `${r.id}-contractor`,
    size: r.size,
    meter_input: "",
  }));
}

function buildAccContractorRows() {
  return ACC_BLOCK_ROWS.map((r) => ({
    id: `${r.id}-contractor`,
    size: r.size,
    m3_input: "",
  }));
}

function getSteelPiecesPerTonne(size) {
  const normalized = String(size || "").toLowerCase();
  const key = Object.keys(STEEL_CONTRACTOR_PIECES_PER_TONNE).find((k) => normalized.includes(k));
  return key ? STEEL_CONTRACTOR_PIECES_PER_TONNE[key] : null;
}

// Build rows dynamically based on sizes × brands × locations
function buildRows(product) {
  if (!product) return [];
  const rows = [];
  product.sizes.forEach((size, sIdx) => {
    const brand = product.brands[sIdx % product.brands.length];
    const location = product.locations[sIdx % product.locations.length];
    // Price varies by size index
    const priceMul = 1 + (sIdx - product.sizes.length / 2) * 0.03;
    const price = Math.round(product.base_price * priceMul);
    rows.push({
      id: `${product.slug}-${size}`,
      size,
      brand,
      location,
      price,
      price_mt: price,
      unit: "MT",
      quantity: 1,
    });
  });
  return rows;
}

function getDisplayPrice(slug, row, diPipeType = "K7") {
  if (!row) return 0;
  if (slug === "di-pipes" || slug === "pvc-pipes") {
    return diPipeType === "K9" ? Number(row.k9_price) : Number(row.k7_price);
  }
  if (slug?.toLowerCase() === "acc-block") {
    return Number(row.price_per_m3);
  }
  const baseMtPrice = row.price_mt ?? row.price;
  if (slug === "cement" && row.unit === "bag") {
    return Math.round((baseMtPrice / 20) * 100) / 100;
  }
  if (slug === "steel-tmt" && row.unit === "Piece") {
    const piecesPerTonne = getSteelPiecesPerTonne(row.size);
    if (!piecesPerTonne) return Math.round(baseMtPrice * 100) / 100;
    return Math.round((baseMtPrice / piecesPerTonne) * 100) / 100;
  }
  return baseMtPrice;
}

const GUEST_FLOW_RESUME_KEY = "RAILAXIS_GUEST_FLOW_RESUME";

function normalizeProductSlug(slug) {
  const value = String(slug || "").trim();
  if (value === "di-pipes") return "pvc-pipes";
  if (value.toLowerCase() === "acc-block") return "Acc-block";
  return value;
}

function normalizeBrand(brand) {
  return String(brand || "").trim().toLowerCase();
}

function routeSlugFromProductSlug(slug) {
  const value = normalizeProductSlug(slug);
  if (value === "pvc-pipes") return "di-pipes";
  return value;
}

export default function ProductDashboard() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addItem, items } = useCart();
  const { user, loading: authLoading } = useAuth();
  const [product, setProduct] = useState(null);
  const [rows, setRows] = useState([]);
  const [vendorRows, setVendorRows] = useState([]);
  const [vendorDefaultPincode, setVendorDefaultPincode] = useState("");
  const [vendorDefaultPrice, setVendorDefaultPrice] = useState("");
  const [loading, setLoading] = useState(true);
  const [cementType, setCementType] = useState("OPC");
  const [diPipeType, setDiPipeType] = useState("K7");
  const [addingByRow, setAddingByRow] = useState({});
  const [savingVendorRowById, setSavingVendorRowById] = useState({});
  const [steelContractorStep, setSteelContractorStep] = useState(1);
  const [steelContractorRows, setSteelContractorRows] = useState(buildSteelContractorRows());
  const [steelPincode, setSteelPincode] = useState("");
  const [steelLocation, setSteelLocation] = useState("");
  const [steelLocationLoading, setSteelLocationLoading] = useState(false);
  const [steelLocationQuery, setSteelLocationQuery] = useState("");
  const [selectedSteelBrand, setSelectedSteelBrand] = useState("");
  const [cementContractorStep, setCementContractorStep] = useState(1);
  const [cementContractorRows, setCementContractorRows] = useState(buildCementContractorRows());
  const [cementPincode, setCementPincode] = useState("");
  const [cementLocation, setCementLocation] = useState("");
  const [cementLocationLoading, setCementLocationLoading] = useState(false);
  const [cementLocationQuery, setCementLocationQuery] = useState("");
  const [cementOfferRows, setCementOfferRows] = useState([]);
  const [selectedCementBrand, setSelectedCementBrand] = useState("");
  const [diContractorStep, setDiContractorStep] = useState(1);
  const [diContractorRows, setDiContractorRows] = useState(buildDiContractorRows());
  const [diPincode, setDiPincode] = useState("");
  const [diLocation, setDiLocation] = useState("");
  const [diLocationLoading, setDiLocationLoading] = useState(false);
  const [diLocationQuery, setDiLocationQuery] = useState("");
  const [diOfferRows, setDiOfferRows] = useState([]);
  const [selectedDiBrand, setSelectedDiBrand] = useState("");
  const [accContractorStep, setAccContractorStep] = useState(1);
  const [accContractorRows, setAccContractorRows] = useState(buildAccContractorRows());
  const [accPincode, setAccPincode] = useState("");
  const [accLocation, setAccLocation] = useState("");
  const [accLocationLoading, setAccLocationLoading] = useState(false);
  const [accLocationQuery, setAccLocationQuery] = useState("");
  const [accOfferRows, setAccOfferRows] = useState([]);
  const [selectedAccBrand, setSelectedAccBrand] = useState("");
  const isAccBlockPage = slug?.toLowerCase() === "acc-block";
  const productSlug = slug === "di-pipes" ? "pvc-pipes" : (isAccBlockPage ? "Acc-block" : slug);
  const currentNormalizedSlug = normalizeProductSlug(productSlug);
  const assignedVendorSlug = normalizeProductSlug(user?.vendor_product_slug);
  const assignedVendorBrand = String(user?.vendor_brand || "").trim();
  const isVendorRestrictedCategory = user?.role === "vendor" && assignedVendorSlug && currentNormalizedSlug !== assignedVendorSlug;
  const vendorBrandOptions = getBrandsForProduct(productSlug);
  const isDiPipesPage = slug === "di-pipes" || slug === "pvc-pipes";

  const isVendorSteel = slug === "steel-tmt" && user?.role === "vendor";
  const isContractorSteel = slug === "steel-tmt" && (user?.role === "contractor" || !user);
  const isVendorCement = slug === "cement" && user?.role === "vendor";
  const isContractorCement = slug === "cement" && (user?.role === "contractor" || !user);
  const isVendorDiPipes = isDiPipesPage && user?.role === "vendor";
  const isContractorDiPipes = isDiPipesPage && (user?.role === "contractor" || !user);
  const isVendorAccBlock = isAccBlockPage && user?.role === "vendor";
  const isContractorAccBlock = isAccBlockPage && (user?.role === "contractor" || !user);
  const isVendorMaterialsPage = (slug === "steel-tmt" || slug === "cement" || isDiPipesPage || isAccBlockPage) && user?.role === "vendor";
  const isContractorMaterialsPage = (slug === "steel-tmt" || slug === "cement" || isDiPipesPage || isAccBlockPage) && (user?.role === "contractor" || !user);
  const showBaseRowsTable = !isContractorMaterialsPage;
  const vendorVisibleRows = user?.role === "vendor"
    ? vendorRows.filter((row) => {
        const userKeys = [user.id, user.vendor_code].filter(Boolean).map(String);
        const rowKeys = [row.vendor_user_id, row.vendor_id, row.vendor_code].filter(Boolean).map(String);
        const matchesVendor = rowKeys.some((key) => userKeys.includes(key));
        const matchesBrand = !assignedVendorBrand || normalizeBrand(row.brand) === normalizeBrand(assignedVendorBrand);
        return matchesVendor && matchesBrand;
      })
    : vendorRows;
  const showVendorIdentityColumns = user?.role === "admin";
  const vendorProductsColSpan = showVendorIdentityColumns
    ? ((isContractorDiPipes || isContractorAccBlock) ? 7 : 8)
    : 6;
  const vendorAllowedBrands = assignedVendorBrand
    ? [assignedVendorBrand]
    : (vendorBrandOptions.length ? vendorBrandOptions : (product?.brands || []));

  const selectedSteelRows = steelContractorRows.filter((row) => {
    const hasPieces = Number(row.pieces_input) > 0;
    const hasTonnes = Number(row.tonnes_input) > 0;
    return hasPieces || hasTonnes;
  });
  const canContinueSteelContractor = selectedSteelRows.length > 0;
  const steelSelectedPiecesBySize = selectedSteelRows.reduce((acc, row) => {
    const pieces = Number(row.pieces_input || 0);
    if (pieces > 0) acc[row.size] = pieces;
    return acc;
  }, {});
  const selectedCementRows = cementContractorRows.filter((row) => Number(row.bag_input) > 0 || Number(row.mt_input) > 0);
  const canContinueCementContractor = selectedCementRows.length > 0;
  const selectedDiRows = diContractorRows.filter((row) => Number(row.meter_input) > 0);
  const canContinueDiContractor = selectedDiRows.length > 0;
  const selectedAccRows = accContractorRows.filter((row) => Number(row.m3_input) > 0);
  const canContinueAccContractor = selectedAccRows.length > 0;

  useEffect(() => {
    setVendorDefaultPincode("");
    setVendorDefaultPrice("");
  }, [productSlug]);

  useEffect(() => {
    if (authLoading || isVendorRestrictedCategory) return;

    setLoading(true);
    api.get(`/products/${productSlug}`)
      .then(({ data }) => {
        const defaultBrand = user?.role === "vendor"
          ? (assignedVendorBrand || data.brands?.[0] || "N/A")
          : (vendorBrandOptions[0] || data.brands?.[0] || "N/A");
        const defaultPincode = user?.role === "vendor" ? "" : (data.locations?.[0] || "N/A");
        setProduct(data);
        if (slug === "cement") {
          setRows(CEMENT_ROWS.map((r) => ({
            ...r,
            brand: defaultBrand,
            location: defaultPincode,
          })));
        } else if (isDiPipesPage) {
          setRows(DI_PIPES_ROWS.map((r) => ({
            ...r,
            brand: defaultBrand,
            location: defaultPincode,
          })));
        } else if (isAccBlockPage) {
          setRows(ACC_BLOCK_ROWS.map((r) => ({
            ...r,
            brand: defaultBrand,
            location: defaultPincode,
          })));
        } else {
          setRows(buildRows(data).map((r) => ({ ...r, brand: defaultBrand, location: defaultPincode })));
        }
      })
      .catch(() => toast.error("Product not found"))
      .finally(() => setLoading(false));
  }, [authLoading, isVendorRestrictedCategory, slug, productSlug, isDiPipesPage, isAccBlockPage, vendorBrandOptions, user?.role, assignedVendorBrand]);

  useEffect(() => {
    if (authLoading || isVendorRestrictedCategory) {
      setVendorRows([]);
      return;
    }

    if (!((slug === "steel-tmt" || slug === "cement" || isDiPipesPage || isAccBlockPage) && (user?.role === "vendor" || user?.role === "contractor" || user?.role === "admin"))) {
      setVendorRows([]);
      return;
    }
    api.get(`/products/${productSlug}/vendor-products`)
      .then(({ data }) => setVendorRows((data || []).map((r) => ({
        ...r,
        selected_unit: r.unit || "MT",
        selected_quantity: String(r.quantity ?? 1),
      }))))
      .catch(() => setVendorRows([]));
  }, [authLoading, isVendorRestrictedCategory, slug, productSlug, isDiPipesPage, isAccBlockPage, user?.role]);

  useEffect(() => {
    if (!isContractorSteel) return;
    setSteelContractorStep(1);
    setSteelContractorRows(buildSteelContractorRows());
    setSteelPincode("");
    setSteelLocation("");
    setSteelLocationLoading(false);
    setSteelLocationQuery("");
    setSelectedSteelBrand("");
  }, [isContractorSteel]);

  useEffect(() => {
    if (!isContractorCement) return;
    setCementContractorStep(1);
    setCementContractorRows(buildCementContractorRows());
    setCementPincode("");
    setCementLocation("");
    setCementLocationLoading(false);
    setCementLocationQuery("");
    setCementOfferRows([]);
    setSelectedCementBrand("");
  }, [isContractorCement, cementType]);

  useEffect(() => {
    if (!isContractorDiPipes) return;
    setDiContractorStep(1);
    setDiContractorRows(buildDiContractorRows());
    setDiPincode("");
    setDiLocation("");
    setDiLocationLoading(false);
    setDiLocationQuery("");
    setDiOfferRows([]);
    setSelectedDiBrand("");
  }, [isContractorDiPipes, diPipeType]);

  useEffect(() => {
    if (!isContractorAccBlock) return;
    setAccContractorStep(1);
    setAccContractorRows(buildAccContractorRows());
    setAccPincode("");
    setAccLocation("");
    setAccLocationLoading(false);
    setAccLocationQuery("");
    setAccOfferRows([]);
    setSelectedAccBrand("");
  }, [isContractorAccBlock]);

  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(location.search);
    if (params.get("resume") !== "1") return;

    try {
      const raw = sessionStorage.getItem(GUEST_FLOW_RESUME_KEY);
      if (!raw) return;
      const data = JSON.parse(raw);
      if (!data || data.slug !== slug) return;

      if (data.flow === "steel" && Array.isArray(data.steelContractorRows)) {
        setSteelContractorRows(data.steelContractorRows);
        setSteelContractorStep(2);
      }
      if (data.flow === "cement" && Array.isArray(data.cementContractorRows)) {
        setCementContractorRows(data.cementContractorRows);
        if (data.cementType) setCementType(data.cementType);
        setCementContractorStep(2);
      }
      if (data.flow === "di" && Array.isArray(data.diContractorRows)) {
        setDiContractorRows(data.diContractorRows);
        if (data.diPipeType) setDiPipeType(data.diPipeType);
        setDiContractorStep(2);
      }
      if (data.flow === "acc" && Array.isArray(data.accContractorRows)) {
        setAccContractorRows(data.accContractorRows);
        setAccContractorStep(2);
      }
    } catch {}

    sessionStorage.removeItem(GUEST_FLOW_RESUME_KEY);
    navigate(`/products/${slug}`, { replace: true });
  }, [user, location.search, slug, navigate]);

  useEffect(() => {
    if (!isVendorRestrictedCategory) return;
    navigate(`/products/${routeSlugFromProductSlug(assignedVendorSlug)}`, { replace: true });
  }, [isVendorRestrictedCategory, assignedVendorSlug, navigate]);

  useEffect(() => {
    if (!isContractorSteel || steelContractorStep !== 2) return;
    if (!/^\d{6}$/.test(steelPincode)) {
      setSteelLocation("");
      return;
    }

    let isActive = true;
    setSteelLocationLoading(true);
    fetch(`https://api.postalpincode.in/pincode/${steelPincode}`)
      .then((res) => res.json())
      .then((data) => {
        if (!isActive) return;
        const first = data?.[0];
        const office = first?.PostOffice?.[0];
        if (first?.Status === "Success" && office) {
          const locationText = [office.District, office.State].filter(Boolean).join(", ");
          setSteelLocation(locationText || office.Name || "");
          setSteelLocationQuery(String(office.District || office.Name || "").trim());
        } else {
          setSteelLocation("");
          setSteelLocationQuery("");
        }
      })
      .catch(() => {
        if (!isActive) return;
        setSteelLocation("");
        setSteelLocationQuery("");
      })
      .finally(() => {
        if (isActive) setSteelLocationLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [isContractorSteel, steelContractorStep, steelPincode]);

  useEffect(() => {
    if (!isContractorSteel || steelContractorStep !== 2 || !/^\d{6}$/.test(steelPincode)) return;
    api.get(`/products/${productSlug}/vendor-products?location=${encodeURIComponent(steelPincode)}`)
      .then(({ data }) => {
        setVendorRows((data || []).map((r) => ({
          ...r,
          selected_unit: r.unit || "MT",
          selected_quantity: String(r.quantity ?? 1),
        })));
        setSelectedSteelBrand("");
      })
      .catch(() => setVendorRows([]));
  }, [isContractorSteel, steelContractorStep, steelPincode, productSlug]);

  useEffect(() => {
    if (!isContractorCement || cementContractorStep !== 2) return;
    if (!/^\d{6}$/.test(cementPincode)) {
      setCementLocation("");
      setCementLocationQuery("");
      return;
    }

    let isActive = true;
    setCementLocationLoading(true);
    fetch(`https://api.postalpincode.in/pincode/${cementPincode}`)
      .then((res) => res.json())
      .then((data) => {
        if (!isActive) return;
        const first = data?.[0];
        const office = first?.PostOffice?.[0];
        if (first?.Status === "Success" && office) {
          const locationText = [office.District, office.State].filter(Boolean).join(", ");
          setCementLocation(locationText || office.Name || "");
          setCementLocationQuery(String(office.District || office.Name || "").trim());
        } else {
          setCementLocation("");
          setCementLocationQuery("");
        }
      })
      .catch(() => {
        if (!isActive) return;
        setCementLocation("");
        setCementLocationQuery("");
      })
      .finally(() => {
        if (isActive) setCementLocationLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [isContractorCement, cementContractorStep, cementPincode]);

  useEffect(() => {
    if (!isContractorCement || cementContractorStep !== 2) return;
    const path = /^\d{6}$/.test(cementPincode)
      ? `/products/${productSlug}/vendor-products?location=${encodeURIComponent(cementPincode)}`
      : `/products/${productSlug}/vendor-products`;

    api.get(path)
      .then(({ data }) => {
        const rows = (data || []).map((r) => ({ ...r }));
        // Fallback: if location filter returns nothing, show all brand offers.
        if (/^\d{6}$/.test(cementPincode) && rows.length === 0) {
          return api.get(`/products/${productSlug}/vendor-products`).then(({ data: allData }) => {
            setCementOfferRows((allData || []).map((r) => ({ ...r })));
            setSelectedCementBrand("");
          });
        }
        setCementOfferRows(rows);
        setSelectedCementBrand("");
      })
      .catch(() => setCementOfferRows([]));
  }, [isContractorCement, cementContractorStep, cementPincode, productSlug]);

  useEffect(() => {
    if (!isContractorDiPipes || diContractorStep !== 2) return;
    if (!/^\d{6}$/.test(diPincode)) {
      setDiLocation("");
      setDiLocationQuery("");
      return;
    }
    let isActive = true;
    setDiLocationLoading(true);
    fetch(`https://api.postalpincode.in/pincode/${diPincode}`)
      .then((res) => res.json())
      .then((data) => {
        if (!isActive) return;
        const first = data?.[0];
        const office = first?.PostOffice?.[0];
        if (first?.Status === "Success" && office) {
          const locationText = [office.District, office.State].filter(Boolean).join(", ");
          setDiLocation(locationText || office.Name || "");
          setDiLocationQuery(String(office.District || office.Name || "").trim());
        } else {
          setDiLocation("");
          setDiLocationQuery("");
        }
      })
      .catch(() => {
        if (!isActive) return;
        setDiLocation("");
        setDiLocationQuery("");
      })
      .finally(() => {
        if (isActive) setDiLocationLoading(false);
      });
    return () => { isActive = false; };
  }, [isContractorDiPipes, diContractorStep, diPincode]);

  useEffect(() => {
    if (!isContractorDiPipes || diContractorStep !== 2) return;
    const path = /^\d{6}$/.test(diPincode)
      ? `/products/${productSlug}/vendor-products?location=${encodeURIComponent(diPincode)}`
      : `/products/${productSlug}/vendor-products`;
    api.get(path)
      .then(({ data }) => {
        const rows = (data || []).map((r) => ({ ...r }));
        if (/^\d{6}$/.test(diPincode) && rows.length === 0) {
          return api.get(`/products/${productSlug}/vendor-products`).then(({ data: allData }) => {
            setDiOfferRows((allData || []).map((r) => ({ ...r })));
            setSelectedDiBrand("");
          });
        }
        setDiOfferRows(rows);
        setSelectedDiBrand("");
      })
      .catch(() => setDiOfferRows([]));
  }, [isContractorDiPipes, diContractorStep, diPincode, productSlug]);

  useEffect(() => {
    if (!isContractorAccBlock || accContractorStep !== 2) return;
    if (!/^\d{6}$/.test(accPincode)) {
      setAccLocation("");
      setAccLocationQuery("");
      return;
    }
    let isActive = true;
    setAccLocationLoading(true);
    fetch(`https://api.postalpincode.in/pincode/${accPincode}`)
      .then((res) => res.json())
      .then((data) => {
        if (!isActive) return;
        const first = data?.[0];
        const office = first?.PostOffice?.[0];
        if (first?.Status === "Success" && office) {
          const locationText = [office.District, office.State].filter(Boolean).join(", ");
          setAccLocation(locationText || office.Name || "");
          setAccLocationQuery(String(office.District || office.Name || "").trim());
        } else {
          setAccLocation("");
          setAccLocationQuery("");
        }
      })
      .catch(() => {
        if (!isActive) return;
        setAccLocation("");
        setAccLocationQuery("");
      })
      .finally(() => {
        if (isActive) setAccLocationLoading(false);
      });
    return () => { isActive = false; };
  }, [isContractorAccBlock, accContractorStep, accPincode]);

  useEffect(() => {
    if (!isContractorAccBlock || accContractorStep !== 2) return;
    const path = /^\d{6}$/.test(accPincode)
      ? `/products/${productSlug}/vendor-products?location=${encodeURIComponent(accPincode)}`
      : `/products/${productSlug}/vendor-products`;
    api.get(path)
      .then(({ data }) => {
        const rows = (data || []).map((r) => ({ ...r }));
        if (/^\d{6}$/.test(accPincode) && rows.length === 0) {
          return api.get(`/products/${productSlug}/vendor-products`).then(({ data: allData }) => {
            setAccOfferRows((allData || []).map((r) => ({ ...r })));
            setSelectedAccBrand("");
          });
        }
        setAccOfferRows(rows);
        setSelectedAccBrand("");
      })
      .catch(() => setAccOfferRows([]));
  }, [isContractorAccBlock, accContractorStep, accPincode, productSlug]);

  const updateRow = (id, patch) => {
    setRows((rs) => rs.map((r) => r.id === id ? { ...r, ...patch } : r));
  };

  const applyVendorDefaultPincode = (value) => {
    const nextValue = digitsOnly(value, 6);
    setVendorDefaultPincode(nextValue);
    if (!nextValue) return;
    setRows((prev) => prev.map((row) => ({ ...row, location: nextValue })));
  };

  const applyVendorDefaultPrice = (value) => {
    const nextValue = decimalOnly(value, 2);
    setVendorDefaultPrice(nextValue);
    if (!nextValue) return;

    setRows((prev) => prev.map((row) => {
      if (slug === "cement") {
        return { ...row, price: nextValue, price_mt: nextValue };
      }
      if (isDiPipesPage) {
        return diPipeType === "K9"
          ? { ...row, k9_price: nextValue }
          : { ...row, k7_price: nextValue };
      }
      if (isAccBlockPage) {
        return { ...row, price_per_m3: nextValue };
      }
      return { ...row, price: nextValue, price_mt: nextValue };
    }));
  };

  const vendorDefaultPriceLabel = isDiPipesPage
    ? `${diPipeType} Price`
    : isAccBlockPage
      ? "Price / m3"
      : "Price / MT";

  const updateSteelContractorRow = (id, field, rawValue) => {
    const normalized = field === "pieces" ? integerOnly(rawValue) : decimalOnly(rawValue, 3);
    setSteelContractorRows((prev) => prev.map((row) => {
      if (row.id !== id) return row;

      if (!normalized) {
        return { ...row, pieces_input: "", tonnes_input: "" };
      }

      const inputNum = Number(normalized);
      if (!Number.isFinite(inputNum) || inputNum < 0) return row;

      if (field === "pieces") {
        return {
          ...row,
          pieces_input: normalized,
          tonnes_input: trimNumericString(inputNum / row.pieces_per_tonne),
        };
      }

      return {
        ...row,
        tonnes_input: normalized,
        pieces_input: String(Math.round(inputNum * row.pieces_per_tonne)),
      };
    }));
  };

  const updateCementContractorRow = (id, field, rawValue) => {
    const normalized = field === "bag" ? integerOnly(rawValue) : decimalOnly(rawValue, 3);
    setCementContractorRows((prev) => prev.map((row) => {
      if (row.id !== id) return row;
      if (!normalized) {
        return { ...row, bag_input: "", mt_input: "", input_mode: field };
      }
      const inputNum = Number(normalized);
      if (!Number.isFinite(inputNum) || inputNum < 0) return row;

      if (field === "bag") {
        return {
          ...row,
          input_mode: "bag",
          bag_input: normalized,
          mt_input: trimNumericString(inputNum / 20),
        };
      }
      return {
        ...row,
        input_mode: "MT",
        mt_input: normalized,
        bag_input: String(Math.round(inputNum * 20)),
      };
    }));
  };

  const updateDiContractorRow = (id, rawValue) => {
    const normalized = decimalOnly(rawValue, 3);
    setDiContractorRows((prev) => prev.map((row) => (
      row.id === id ? { ...row, meter_input: normalized } : row
    )));
  };

  const updateAccContractorRow = (id, rawValue) => {
    const normalized = decimalOnly(rawValue, 3);
    setAccContractorRows((prev) => prev.map((row) => (
      row.id === id ? { ...row, m3_input: normalized } : row
    )));
  };

  const handleAdd = (row) => {
    if (row.quantity < 1) {
      toast.error("Enter a valid quantity");
      return;
    }
    const effectivePrice = getDisplayPrice(slug, row, diPipeType);
    addItem({
      product_slug: product.slug,
      product_name: product.name,
      brand: row.brand,
      size: slug === "cement" ? `${cementType} - ${row.grade}` : (isDiPipesPage ? `${row.size} - ${diPipeType}` : row.size),
      location: row.location || (slug === "cement" ? (product.locations?.[0] || "N/A") : ((isDiPipesPage || isAccBlockPage) ? (product.locations?.[0] || "N/A") : "N/A")),
      unit: isDiPipesPage ? "m" : (isAccBlockPage ? "m3" : row.unit),
      price: effectivePrice,
      quantity: Number(row.quantity),
    });
    toast.success(`${product.name} ${slug === "cement" ? row.grade : (isDiPipesPage ? `${row.size} ${diPipeType}` : row.size)} added to cart`);
  };

  const handleVendorAddProduct = async (row) => {
    if (!isVendorMaterialsPage) return;
    if (row.quantity < 1) {
      toast.error("Enter a valid quantity");
      return;
    }
    if (!/^\d{6}$/.test(String(row.location || ""))) {
      toast.error("Enter valid 6-digit pincode");
      return;
    }
    const effectivePrice = getDisplayPrice(slug, row, diPipeType);
    setAddingByRow((prev) => ({ ...prev, [row.id]: true }));
    try {
      const { data } = await api.post(`/products/${productSlug}/vendor-products`, {
        size: slug === "cement" ? `${cementType} - ${row.grade}` : row.size,
        brand: row.brand,
        location: row.location || (slug === "cement" ? (product.locations?.[0] || "N/A") : ((isDiPipesPage || isAccBlockPage) ? (product.locations?.[0] || "N/A") : "N/A")),
        price: effectivePrice,
        unit: isDiPipesPage ? diPipeType : (isAccBlockPage ? "m3" : row.unit),
        quantity: Number(row.quantity),
      });
      setVendorRows((prev) => [{
        ...data,
        selected_unit: data.unit || (isDiPipesPage ? diPipeType : "MT"),
        selected_quantity: String(data.quantity ?? 1),
      }, ...prev]);
      toast.success("Product row added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add product");
    } finally {
      setAddingByRow((prev) => ({ ...prev, [row.id]: false }));
    }
  };

  const updateVendorRowUi = (id, patch) => {
    setVendorRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const getVendorRowDisplayPrice = (row) => {
    const selectedUnit = row.selected_unit || row.unit;
    if (slug === "steel-tmt") {
      const piecesPerTonne = getSteelPiecesPerTonne(row.size);
      if (!piecesPerTonne) return Math.round(Number(row.price) * 100) / 100;
      const baseMtPrice = row.unit === "Piece" ? Number(row.price) * piecesPerTonne : Number(row.price);
      if (selectedUnit === "Piece") {
        return Math.round((baseMtPrice / piecesPerTonne) * 100) / 100;
      }
      return Math.round(baseMtPrice * 100) / 100;
    }
    if (slug === "cement") {
      const baseMtPrice = row.unit === "bag" ? Number(row.price) * 20 : Number(row.price);
      if (selectedUnit === "bag") {
        return Math.round((baseMtPrice / 20) * 100) / 100;
      }
      return Math.round(baseMtPrice * 100) / 100;
    }
    return Math.round(Number(row.price) * 100) / 100;
  };

  const saveVendorProductRow = async (row) => {
    if (!isVendorMaterialsPage) return;
    if (!/^\d{6}$/.test(String(row.location || ""))) {
      toast.error("Enter valid 6-digit pincode");
      return;
    }
    const parsedPrice = Number(row.price);
    const parsedQuantity = Number(row.quantity);
    if (!Number.isFinite(parsedPrice) || parsedPrice <= 0) {
      toast.error("Enter valid price");
      return;
    }
    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
      toast.error("Enter valid quantity");
      return;
    }
    setSavingVendorRowById((prev) => ({ ...prev, [row.id]: true }));
    try {
      const { data } = await api.patch(`/products/${productSlug}/vendor-products/${row.id}`, {
        size: row.size,
        brand: row.brand,
        location: row.location,
        price: parsedPrice,
        unit: row.unit,
        quantity: Math.round(parsedQuantity),
      });
      setVendorRows((prev) => prev.map((entry) => (
        entry.id === row.id
          ? {
              ...entry,
              ...data,
              selected_unit: entry.selected_unit || data.unit,
              selected_quantity: entry.selected_quantity || String(data.quantity ?? 1),
            }
          : entry
      )));
      toast.success("Product updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update product");
    } finally {
      setSavingVendorRowById((prev) => ({ ...prev, [row.id]: false }));
    }
  };

  const steelBrandOffers = isContractorSteel ? Object.values(vendorRows.reduce((acc, row) => {
    const brand = String(row.brand || "").trim();
    const size = String(row.size || "").trim();
    if (!brand || !size) return acc;
    const piecesPerTonne = getSteelPiecesPerTonne(size);
    if (!piecesPerTonne) return acc;
    const rawPrice = Number(row.price);
    if (!Number.isFinite(rawPrice) || rawPrice <= 0) return acc;
    const unitPricePerPiece = row.unit === "Piece" ? rawPrice : (rawPrice / piecesPerTonne);

    if (!acc[brand]) acc[brand] = { brand, rowsBySize: {} };
    const existing = acc[brand].rowsBySize[size];
    if (!existing || unitPricePerPiece < existing.unitPricePerPiece) {
      acc[brand].rowsBySize[size] = { row, unitPricePerPiece };
    }
    return acc;
  }, {})).map((offer) => {
    const lineItems = Object.entries(steelSelectedPiecesBySize).flatMap(([size, pieces]) => {
      const picked = offer.rowsBySize[size];
      if (!picked) return [];
      const piecesPerTonne = getSteelPiecesPerTonne(size);
      if (!piecesPerTonne) return [];
      const lineTotal = picked.unitPricePerPiece * pieces;
      return [{ size, pieces, tonnes: pieces / piecesPerTonne, lineTotal, row: picked.row, unitPrice: picked.unitPricePerPiece }];
    });
    return { ...offer, lineItems, subTotal: lineItems.reduce((sum, item) => sum + item.lineTotal, 0) };
  }).filter((offer) => offer.lineItems.length > 0) : [];

  const selectedSteelOffer = steelBrandOffers.find((offer) => offer.brand === selectedSteelBrand) || null;

  useEffect(() => {
    if (!isContractorSteel) return;
    if (steelBrandOffers.length === 0) {
      setSelectedSteelBrand("");
      return;
    }
    if (!selectedSteelBrand || !steelBrandOffers.some((v) => v.brand === selectedSteelBrand)) {
      setSelectedSteelBrand(steelBrandOffers[0].brand);
    }
  }, [isContractorSteel, steelBrandOffers, selectedSteelBrand]);

  const addSelectedSteelOfferToCart = () => {
    if (!selectedSteelOffer) return;
    selectedSteelOffer.lineItems.forEach((item) => {
      addItem({
        product_slug: product.slug,
        product_name: product.name,
        brand: item.row.brand,
        size: item.size,
        location: steelLocation || item.row.location || "N/A",
        unit: "Piece",
        price: Number((item.lineTotal / item.pieces).toFixed(2)),
        quantity: item.pieces,
        pieces_per_tonne: getSteelPiecesPerTonne(item.size),
        tonnes_quantity: item.tonnes,
        vendor_id: item.row.vendor_user_id || item.row.vendor_id || item.row.vendor_code || null,
        vendor_name: item.row.vendor_name || null,
        vendor_company: item.row.vendor_company || null,
      });
    });
    toast.success("Selected steel items added to cart");
    navigate("/orders");
  };

  const cementRequestedBySize = selectedCementRows.reduce((acc, row) => {
    const key = `${cementType} - ${row.grade}`;
    const qtyBag = Number(row.bag_input || 0);
    const qtyMt = Number(row.mt_input || 0);
    acc[key] = {
      mode: row.input_mode || "MT",
      qtyBag,
      qtyMt,
    };
    return acc;
  }, {});

  const cementBrandOffers = isContractorCement ? Object.values(cementOfferRows.reduce((acc, row) => {
    const brand = String(row.brand || "").trim();
    const size = String(row.size || "").trim();
    if (!brand || !size) return acc;
    const rawPrice = Number(row.price);
    if (!Number.isFinite(rawPrice) || rawPrice <= 0) return acc;
    const baseMtPrice = row.unit === "bag" ? rawPrice * 20 : rawPrice;

    if (!acc[brand]) acc[brand] = { brand, rowsBySize: {} };
    const existing = acc[brand].rowsBySize[size];
    if (!existing || baseMtPrice < existing.baseMtPrice) {
      acc[brand].rowsBySize[size] = { row, baseMtPrice };
    }
    return acc;
  }, {})).map((offer) => {
    const lineItems = Object.entries(cementRequestedBySize).flatMap(([sizeKey, req]) => {
      const picked = offer.rowsBySize[sizeKey];
      if (!picked) return [];
      if (req.mode === "bag") {
        const unitPrice = picked.baseMtPrice / 20;
        const lineTotal = unitPrice * req.qtyBag;
        return [{ size: sizeKey, lineTotal, qty: req.qtyBag, qtyUnit: "bag", unitPrice, row: picked.row }];
      }
      const unitPrice = picked.baseMtPrice;
      const lineTotal = unitPrice * req.qtyMt;
      return [{ size: sizeKey, lineTotal, qty: req.qtyMt, qtyUnit: "MT", unitPrice, row: picked.row }];
    });
    return { ...offer, lineItems, subTotal: lineItems.reduce((sum, item) => sum + item.lineTotal, 0) };
  }).filter((offer) => offer.lineItems.length > 0) : [];

  const selectedCementOffer = cementBrandOffers.find((offer) => offer.brand === selectedCementBrand) || null;

  useEffect(() => {
    if (!isContractorCement) return;
    if (cementBrandOffers.length === 0) {
      setSelectedCementBrand("");
      return;
    }
    if (!selectedCementBrand || !cementBrandOffers.some((v) => v.brand === selectedCementBrand)) {
      setSelectedCementBrand(cementBrandOffers[0].brand);
    }
  }, [isContractorCement, cementBrandOffers, selectedCementBrand]);

  const addSelectedCementOfferToCart = () => {
    if (!selectedCementOffer) return;
    selectedCementOffer.lineItems.forEach((item) => {
      addItem({
        product_slug: product.slug,
        product_name: product.name,
        brand: item.row.brand,
        size: item.size,
        location: cementLocation || item.row.location || "N/A",
        unit: item.qtyUnit,
        price: Number(item.unitPrice.toFixed(2)),
        quantity: Number(item.qty),
        tonnes_quantity: item.qtyUnit === "bag" ? Number(item.qty) / 20 : Number(item.qty),
        vendor_id: item.row.vendor_user_id || item.row.vendor_id || item.row.vendor_code || null,
        vendor_name: item.row.vendor_name || null,
        vendor_company: item.row.vendor_company || null,
      });
    });
    toast.success("Selected cement items added to cart");
    navigate("/orders");
  };

  const diRequestedMetersBySize = selectedDiRows.reduce((acc, row) => {
    const meters = Number(row.meter_input || 0);
    if (meters > 0) acc[row.size] = meters;
    return acc;
  }, {});

  const diBrandOffers = isContractorDiPipes ? Object.values(diOfferRows.reduce((acc, row) => {
    if (String(row.unit || "").trim() !== diPipeType) return acc;
    const brand = String(row.brand || "").trim();
    const size = String(row.size || "").trim();
    if (!brand || !size) return acc;
    if (!acc[brand]) acc[brand] = { brand, rowsBySize: {} };
    const existing = acc[brand].rowsBySize[size];
    const existingPrice = existing ? Number(existing.price) : Infinity;
    if (!existing || Number(row.price) < existingPrice) acc[brand].rowsBySize[size] = row;
    return acc;
  }, {})).map((offer) => {
    const lineItems = Object.entries(diRequestedMetersBySize).flatMap(([sizeKey, meters]) => {
      const row = offer.rowsBySize[sizeKey];
      if (!row) return [];
      const unitPrice = Number(row.price);
      if (!Number.isFinite(unitPrice) || unitPrice <= 0) return [];
      const lineTotal = unitPrice * meters;
      return [{ size: sizeKey, lineTotal, qty: meters, qtyUnit: "m", unitPrice, row }];
    });
    return { ...offer, lineItems, subTotal: lineItems.reduce((sum, item) => sum + item.lineTotal, 0) };
  }).filter((offer) => offer.lineItems.length > 0) : [];

  const selectedDiOffer = diBrandOffers.find((offer) => offer.brand === selectedDiBrand) || null;

  useEffect(() => {
    if (!isContractorDiPipes) return;
    if (diBrandOffers.length === 0) {
      setSelectedDiBrand("");
      return;
    }
    if (!selectedDiBrand || !diBrandOffers.some((v) => v.brand === selectedDiBrand)) {
      setSelectedDiBrand(diBrandOffers[0].brand);
    }
  }, [isContractorDiPipes, diBrandOffers, selectedDiBrand]);

  const addSelectedDiOfferToCart = () => {
    if (!selectedDiOffer) return;
    selectedDiOffer.lineItems.forEach((item) => {
      addItem({
        product_slug: product.slug,
        product_name: product.name,
        brand: item.row.brand,
        size: `${item.size} - ${diPipeType}`,
        location: diLocation || item.row.location || "N/A",
        unit: "m",
        price: Number(item.unitPrice.toFixed(2)),
        quantity: Number(item.qty),
        vendor_id: item.row.vendor_user_id || item.row.vendor_id || item.row.vendor_code || null,
        vendor_name: item.row.vendor_name || null,
        vendor_company: item.row.vendor_company || null,
      });
    });
    toast.success("Selected DI pipe items added to cart");
    navigate("/orders");
  };

  const accRequestedM3BySize = selectedAccRows.reduce((acc, row) => {
    const m3 = Number(row.m3_input || 0);
    if (m3 > 0) acc[row.size] = m3;
    return acc;
  }, {});

  const accBrandOffers = isContractorAccBlock ? Object.values(accOfferRows.reduce((acc, row) => {
    const brand = String(row.brand || "").trim();
    const size = String(row.size || "").trim();
    if (!brand || !size) return acc;
    if (!acc[brand]) acc[brand] = { brand, rowsBySize: {} };
    const existing = acc[brand].rowsBySize[size];
    const existingPrice = existing ? Number(existing.price) : Infinity;
    if (!existing || Number(row.price) < existingPrice) acc[brand].rowsBySize[size] = row;
    return acc;
  }, {})).map((offer) => {
    const lineItems = Object.entries(accRequestedM3BySize).flatMap(([sizeKey, m3]) => {
      const row = offer.rowsBySize[sizeKey];
      if (!row) return [];
      const unitPrice = Number(row.price);
      if (!Number.isFinite(unitPrice) || unitPrice <= 0) return [];
      const lineTotal = unitPrice * m3;
      return [{ size: sizeKey, lineTotal, qty: m3, qtyUnit: "m3", unitPrice, row }];
    });
    return { ...offer, lineItems, subTotal: lineItems.reduce((sum, item) => sum + item.lineTotal, 0) };
  }).filter((offer) => offer.lineItems.length > 0) : [];

  const selectedAccOffer = accBrandOffers.find((offer) => offer.brand === selectedAccBrand) || null;

  useEffect(() => {
    if (!isContractorAccBlock) return;
    if (accBrandOffers.length === 0) {
      setSelectedAccBrand("");
      return;
    }
    if (!selectedAccBrand || !accBrandOffers.some((v) => v.brand === selectedAccBrand)) {
      setSelectedAccBrand(accBrandOffers[0].brand);
    }
  }, [isContractorAccBlock, accBrandOffers, selectedAccBrand]);

  const addSelectedAccOfferToCart = () => {
    if (!selectedAccOffer) return;
    selectedAccOffer.lineItems.forEach((item) => {
      addItem({
        product_slug: product.slug,
        product_name: product.name,
        brand: item.row.brand,
        size: item.size,
        location: accLocation || item.row.location || "N/A",
        unit: "m3",
        price: Number(item.unitPrice.toFixed(2)),
        quantity: Number(item.qty),
        vendor_id: item.row.vendor_user_id || item.row.vendor_id || item.row.vendor_code || null,
        vendor_name: item.row.vendor_name || null,
        vendor_company: item.row.vendor_company || null,
      });
    });
    toast.success("Selected ACC Block items added to cart");
    navigate("/orders");
  };

  const redirectGuestToLoginAndResume = (payload) => {
    if (user) return false;
    sessionStorage.setItem(GUEST_FLOW_RESUME_KEY, JSON.stringify({ slug, ...payload }));
    toast.info("Please login to continue");
    const redirectTo = `/products/${slug}?resume=1`;
    navigate(buildLoginPath(redirectTo));
    return true;
  };

  const isContractorSelectionStep = (
    (isContractorSteel && steelContractorStep === 1) ||
    (isContractorCement && cementContractorStep === 1) ||
    (isContractorDiPipes && diContractorStep === 1) ||
    (isContractorAccBlock && accContractorStep === 1)
  );

  const canContinueContractorSelection = (
    (isContractorSteel && canContinueSteelContractor) ||
    (isContractorCement && canContinueCementContractor) ||
    (isContractorDiPipes && canContinueDiContractor) ||
    (isContractorAccBlock && canContinueAccContractor)
  );

  const continueContractorSelection = () => {
    if (isContractorSteel) {
      if (redirectGuestToLoginAndResume({ flow: "steel", steelContractorRows })) return;
      setSteelContractorStep(2);
      return;
    }
    if (isContractorCement) {
      if (redirectGuestToLoginAndResume({ flow: "cement", cementType, cementContractorRows })) return;
      setCementContractorStep(2);
      return;
    }
    if (isContractorDiPipes) {
      if (redirectGuestToLoginAndResume({ flow: "di", diPipeType, diContractorRows })) return;
      setDiContractorStep(2);
      return;
    }
    if (isContractorAccBlock) {
      if (redirectGuestToLoginAndResume({ flow: "acc", accContractorRows })) return;
      setAccContractorStep(2);
    }
  };

  const floatingCartAction = (() => {
    if (user?.role === "vendor" || !product) return null;
    if (isContractorSelectionStep) {
      return {
        disabled: !canContinueContractorSelection,
        onClick: continueContractorSelection,
        testId: "contractor-floating-add-to-cart-btn",
      };
    }
    if (isContractorSteel && steelContractorStep === 2) {
      return {
        disabled: !selectedSteelOffer,
        onClick: addSelectedSteelOfferToCart,
        testId: "steel-floating-add-to-cart-btn",
      };
    }
    if (isContractorCement && cementContractorStep === 2) {
      return {
        disabled: !selectedCementOffer,
        onClick: addSelectedCementOfferToCart,
        testId: "cement-floating-add-to-cart-btn",
      };
    }
    if (isContractorDiPipes && diContractorStep === 2) {
      return {
        disabled: !selectedDiOffer,
        onClick: addSelectedDiOfferToCart,
        testId: "di-floating-add-to-cart-btn",
      };
    }
    if (isContractorAccBlock && accContractorStep === 2) {
      return {
        disabled: !selectedAccOffer,
        onClick: addSelectedAccOfferToCart,
        testId: "acc-floating-add-to-cart-btn",
      };
    }
    return null;
  })();

  const renderViewCartButton = () => (
    <button
      onClick={() => navigate("/orders")}
      className="inline-flex items-center gap-2 bg-[#111827] text-white px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-[#111827]/85"
      data-testid="view-cart-btn"
    >
      <ShoppingCart className="w-4 h-4" />
      View Cart ({items.length})
    </button>
  );

  return (
    <div className={`bg-white min-h-screen ${floatingCartAction ? "pb-28 md:pb-32" : ""}`}>
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16" data-testid="product-dashboard">
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-sm text-[#4B5563] hover:text-[#C8A96A] mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Categories
        </button>

        {authLoading || loading ? (
          <div className="py-20 text-center text-[#4B5563]">Loading product…</div>
        ) : product ? (
          <>
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
              <div>
                <div className="section-label">{product.category}</div>
                <h1 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl text-[#111827] mt-2 tracking-tight" data-testid="product-title">
                  {product.name}
                </h1>
                <p className="text-[#4B5563] mt-3 max-w-xl">
                  Verified stock from top mills. Live market rates — lock the deal at your delivery location.
                </p>
              </div>
              {user?.role !== "vendor" && !floatingCartAction && renderViewCartButton()}
            </div>

            {isContractorSteel && (
              <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-6">
                {steelContractorStep === 1 ? (
                  <>
                    <h2 className="text-xl md:text-2xl font-bold text-[#111827]">Select size</h2>
                    <p className="text-sm text-[#4B5563] mt-1 mb-5">Enter Pieces or Tonnes for each required TMT size.</p>
                    <div className="space-y-3">
                      {steelContractorRows.map((row) => {
                        const selected = Number(row.pieces_input) > 0 || Number(row.tonnes_input) > 0;
                        return (
                          <div
                            key={row.id}
                            className={`border rounded-lg p-3 md:p-4 ${selected ? "border-blue-500 bg-blue-50/30" : "border-gray-200"}`}
                          >
                            <div className="grid grid-cols-[28px_minmax(0,1fr)] lg:grid-cols-[28px_1fr_180px_36px_180px] items-center gap-2 md:gap-3">
                              <div className={`w-7 h-7 rounded border text-sm flex items-center justify-center font-bold ${selected ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-gray-300 text-transparent"}`}>
                                ✓
                              </div>
                              <div className="font-semibold text-[#111827]">{row.size} TMT Bars</div>
                              <div className="col-span-2 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 lg:contents">
                                <input
                                  {...integerInputProps}
                                  value={row.pieces_input}
                                  onChange={(e) => updateSteelContractorRow(row.id, "pieces", e.target.value)}
                                  placeholder="Enter Pieces"
                                  className="w-full min-w-0 bg-white border border-gray-300 rounded-md px-2 sm:px-3 py-2 text-sm gold-focus"
                                />
                                <div className="text-center text-xs sm:text-sm text-[#6B7280] font-semibold">OR</div>
                                <input
                                  {...decimalInputProps}
                                  value={row.tonnes_input}
                                  onChange={(e) => updateSteelContractorRow(row.id, "tonnes", e.target.value)}
                                  placeholder="Enter Tonnes"
                                  className="w-full min-w-0 bg-white border border-gray-300 rounded-md px-2 sm:px-3 py-2 text-sm gold-focus"
                                />
                              </div>
                            </div>
                            <div className="text-xs text-[#6B7280] mt-2">
                              1 tonne = {row.pieces_per_tonne} pieces
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <>
                    <h2 className="text-xl md:text-2xl font-bold text-[#111827]">Delivery details</h2>
                    <p className="text-sm text-[#4B5563] mt-1 mb-5">
                      Enter pincode to auto-fill location for your selected steel sizes.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs uppercase tracking-wider font-semibold text-[#111827] mb-1.5">Pincode</label>
                        <input
                          type="text"
                          value={steelPincode}
                          onChange={(e) => setSteelPincode(digitsOnly(e.target.value, 6))}
                          {...pincodeInputProps}
                          placeholder="Enter 6-digit pincode"
                          className="w-full bg-white border border-gray-300 rounded-md px-3 py-2.5 text-sm gold-focus"
                        />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-wider font-semibold text-[#111827] mb-1.5">Location</label>
                        <input
                          type="text"
                          value={steelLocationLoading ? "Fetching location..." : steelLocation}
                          readOnly
                          placeholder="Location auto-fills from pincode"
                          className="w-full bg-gray-50 border border-gray-300 rounded-md px-3 py-2.5 text-sm text-[#111827]"
                        />
                      </div>
                    </div>
                    <div className="mt-4 text-xs text-[#4B5563]">
                      Selected rows: {selectedSteelRows.length}
                    </div>

                    <div className="mt-6 grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-6">
                      <div className="space-y-3">
                        {steelLocation && steelBrandOffers.length === 0 && (
                          <div className="border border-gray-200 rounded-lg p-4 text-sm text-[#4B5563]">
                            No brand offers found for this pincode location yet.
                          </div>
                        )}
                        {steelBrandOffers.map((offer) => {
                          const isSelected = selectedSteelBrand === offer.brand;
                          return (
                            <button
                              key={offer.brand}
                              type="button"
                              onClick={() => setSelectedSteelBrand(offer.brand)}
                              className={`w-full text-left border rounded-lg p-4 ${isSelected ? "border-blue-600 bg-blue-50/30" : "border-gray-200 bg-white"}`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <div className="font-bold text-lg text-[#111827]">{offer.brand}</div>
                                </div>
                                <div className="text-right">
                                  <div className="font-bold text-xl text-[#111827]">₹{offer.subTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div>
                                  <div className="text-xs text-[#6B7280]">{isSelected ? "Selected" : "Select"}</div>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>

                      <div className="border border-gray-200 rounded-lg p-4 h-fit">
                        <h3 className="text-xl font-bold text-[#111827]">Best Price Estimator</h3>
                        {!selectedSteelOffer ? (
                          <p className="text-sm text-[#6B7280] mt-3">Select a brand to view estimate details.</p>
                        ) : (
                          <>
                            <div className="text-sm text-[#4B5563] mt-3">Brand</div>
                            <div className="font-semibold text-[#111827]">{selectedSteelOffer.brand || "N/A"}</div>
                            <div className="text-sm text-[#4B5563] mt-1">{steelLocation || "N/A"}</div>
                            <div className="mt-4 space-y-2 text-sm">
                              {selectedSteelOffer.lineItems.map((item) => (
                                <div key={item.size} className="flex justify-between gap-2">
                                  <span>{item.size} TMT Bars</span>
                                  <span className="font-semibold">₹{item.lineTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                                </div>
                              ))}
                            </div>
                            <div className="mt-4 pt-3 border-t border-gray-200 flex justify-between items-center">
                              <span className="font-semibold text-[#111827]">Total</span>
                              <span className="text-xl font-bold text-[#111827]">
                                ₹{selectedSteelOffer.subTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="mt-6 flex justify-start">
                      <button
                        onClick={() => setSteelContractorStep(1)}
                        className="bg-white border border-gray-300 text-[#111827] rounded-md px-5 py-2.5 text-sm font-semibold hover:bg-gray-50"
                      >
                        Back
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {isContractorCement && (
              <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-6">
                {cementContractorStep === 1 ? (
                  <>
                    <div className="flex items-center gap-3 mb-5">
                      <span className="text-sm font-semibold text-[#111827]">Type</span>
                      <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
                        {["OPC", "PPC"].map((type) => (
                          <button
                            key={type}
                            onClick={() => setCementType(type)}
                            className={`px-4 py-2 text-sm font-semibold ${cementType === type ? "bg-[#C8A96A] text-white" : "bg-white text-[#111827]"}`}
                          >
                            {type}
                          </button>
                        ))}
                      </div>
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold text-[#111827]">Select Type/Grade</h2>
                    <p className="text-sm text-[#4B5563] mt-1 mb-5">Select quantity in bag or MT for each grade.</p>
                    <div className="space-y-3">
                      {cementContractorRows.map((row) => {
                        const selected = Number(row.bag_input) > 0 || Number(row.mt_input) > 0;
                        return (
                          <div
                            key={row.id}
                            className={`border rounded-lg p-3 md:p-4 ${selected ? "border-blue-500 bg-blue-50/30" : "border-gray-200"}`}
                          >
                            <div className="grid grid-cols-[28px_minmax(0,1fr)] lg:grid-cols-[28px_1fr_180px_36px_180px] items-center gap-2 md:gap-3">
                              <div className={`w-7 h-7 rounded border text-sm flex items-center justify-center font-bold ${selected ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-gray-300 text-transparent"}`}>
                                ✓
                              </div>
                              <div className="font-semibold text-[#111827]">{cementType} - {row.grade}</div>
                              <div className="col-span-2 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 lg:contents">
                                <select
                                  value={row.bag_input}
                                  onChange={(e) => updateCementContractorRow(row.id, "bag", e.target.value)}
                                  className="w-full min-w-0 bg-white border border-gray-300 rounded-md px-2 sm:px-3 py-2 text-sm gold-focus"
                                >
                                  <option value="">Select Bag</option>
                                  {CEMENT_QTY_OPTIONS.bag.map((quantity) => (
                                    <option key={quantity} value={quantity}>{quantity}</option>
                                  ))}
                                </select>
                                <div className="text-center text-xs sm:text-sm text-[#6B7280] font-semibold">OR</div>
                                <select
                                  value={row.mt_input}
                                  onChange={(e) => updateCementContractorRow(row.id, "MT", e.target.value)}
                                  className="w-full min-w-0 bg-white border border-gray-300 rounded-md px-2 sm:px-3 py-2 text-sm gold-focus"
                                >
                                  <option value="">Select MT</option>
                                  {CEMENT_QTY_OPTIONS.MT.map((quantity) => (
                                    <option key={quantity} value={quantity}>{quantity}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div className="text-xs text-[#6B7280] mt-2">1 MT = 20 bags</div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <>
                    <h2 className="text-xl md:text-2xl font-bold text-[#111827]">Delivery details</h2>
                    <p className="text-sm text-[#4B5563] mt-1 mb-5">
                      Enter pincode to auto-fill location for selected cement requirements.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs uppercase tracking-wider font-semibold text-[#111827] mb-1.5">Pincode</label>
                        <input
                          type="text"
                          value={cementPincode}
                          onChange={(e) => setCementPincode(digitsOnly(e.target.value, 6))}
                          {...pincodeInputProps}
                          placeholder="Enter 6-digit pincode"
                          className="w-full bg-white border border-gray-300 rounded-md px-3 py-2.5 text-sm gold-focus"
                        />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-wider font-semibold text-[#111827] mb-1.5">Location</label>
                        <input
                          type="text"
                          value={cementLocationLoading ? "Fetching location..." : cementLocation}
                          readOnly
                          placeholder="Location auto-fills from pincode"
                          className="w-full bg-gray-50 border border-gray-300 rounded-md px-3 py-2.5 text-sm text-[#111827]"
                        />
                      </div>
                    </div>
                    <div className="mt-4 text-xs text-[#4B5563]">
                      Selected rows: {selectedCementRows.length}
                    </div>

                    <div className="mt-6 grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-6">
                      <div className="space-y-3">
                        {cementLocation && cementBrandOffers.length === 0 && (
                          <div className="border border-gray-200 rounded-lg p-4 text-sm text-[#4B5563]">
                            No brand offers found for this pincode location yet.
                          </div>
                        )}
                        {cementBrandOffers.map((offer) => {
                          const isSelected = selectedCementBrand === offer.brand;
                          return (
                            <button
                              key={offer.brand}
                              type="button"
                              onClick={() => setSelectedCementBrand(offer.brand)}
                              className={`w-full text-left border rounded-lg p-4 ${isSelected ? "border-blue-600 bg-blue-50/30" : "border-gray-200 bg-white"}`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="font-bold text-lg text-[#111827]">{offer.brand}</div>
                                <div className="text-right">
                                  <div className="font-bold text-xl text-[#111827]">₹{offer.subTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div>
                                  <div className="text-xs text-[#6B7280]">{isSelected ? "Selected" : "Select"}</div>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>

                      <div className="border border-gray-200 rounded-lg p-4 h-fit">
                        <h3 className="text-xl font-bold text-[#111827]">Best Price Estimator</h3>
                        {!selectedCementOffer ? (
                          <p className="text-sm text-[#6B7280] mt-3">Select a brand to view estimate details.</p>
                        ) : (
                          <>
                            <div className="text-sm text-[#4B5563] mt-3">Brand</div>
                            <div className="font-semibold text-[#111827]">{selectedCementOffer.brand || "N/A"}</div>
                            <div className="text-sm text-[#4B5563] mt-1">{cementLocation || "N/A"}</div>
                            <div className="mt-4 space-y-2 text-sm">
                              {selectedCementOffer.lineItems.map((item) => (
                                <div key={item.size} className="flex justify-between gap-2">
                                  <span>{item.size} ({item.qty} {item.qtyUnit})</span>
                                  <span className="font-semibold">₹{item.lineTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                                </div>
                              ))}
                            </div>
                            <div className="mt-4 pt-3 border-t border-gray-200 flex justify-between items-center">
                              <span className="font-semibold text-[#111827]">Total</span>
                              <span className="text-xl font-bold text-[#111827]">
                                ₹{selectedCementOffer.subTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="mt-6 flex justify-start">
                      <button
                        onClick={() => setCementContractorStep(1)}
                        className="bg-white border border-gray-300 text-[#111827] rounded-md px-5 py-2.5 text-sm font-semibold hover:bg-gray-50"
                      >
                        Back
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {isContractorDiPipes && (
              <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-6">
                {diContractorStep === 1 ? (
                  <>
                    <div className="flex items-center gap-3 mb-5">
                      <span className="text-sm font-semibold text-[#111827]">Type</span>
                      <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
                        {["K7", "K9"].map((type) => (
                          <button
                            key={type}
                            onClick={() => setDiPipeType(type)}
                            className={`px-4 py-2 text-sm font-semibold ${diPipeType === type ? "bg-[#C8A96A] text-white" : "bg-white text-[#111827]"}`}
                          >
                            {type}
                          </button>
                        ))}
                      </div>
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold text-[#111827]">Select Size</h2>
                    <p className="text-sm text-[#4B5563] mt-1 mb-5">Enter required meters for each size.</p>
                    <div className="space-y-3">
                      {diContractorRows.map((row) => {
                        const selected = Number(row.meter_input) > 0;
                        return (
                          <div key={row.id} className={`border rounded-lg p-3 md:p-4 ${selected ? "border-blue-500 bg-blue-50/30" : "border-gray-200"}`}>
                            <div className="grid grid-cols-[28px_minmax(0,1fr)_minmax(96px,120px)] sm:grid-cols-[28px_minmax(0,1fr)_150px] lg:grid-cols-[28px_1fr_180px] items-center gap-2 md:gap-3">
                              <div className={`w-7 h-7 rounded border text-sm flex items-center justify-center font-bold ${selected ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-gray-300 text-transparent"}`}>✓</div>
                              <div className="font-semibold text-[#111827]">{row.size}</div>
                              <input
                                {...decimalInputProps}
                                value={row.meter_input}
                                onChange={(e) => updateDiContractorRow(row.id, e.target.value)}
                                placeholder="Enter m"
                                className="w-full min-w-0 bg-white border border-gray-300 rounded-md px-2 sm:px-3 py-2 text-sm gold-focus"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <>
                    <h2 className="text-xl md:text-2xl font-bold text-[#111827]">Delivery details</h2>
                    <p className="text-sm text-[#4B5563] mt-1 mb-5">Enter pincode to auto-fill location and show brand offers.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs uppercase tracking-wider font-semibold text-[#111827] mb-1.5">Pincode</label>
                        <input {...pincodeInputProps} value={diPincode} onChange={(e) => setDiPincode(digitsOnly(e.target.value, 6))} placeholder="Enter 6-digit pincode" className="w-full bg-white border border-gray-300 rounded-md px-3 py-2.5 text-sm gold-focus" />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-wider font-semibold text-[#111827] mb-1.5">Location</label>
                        <input type="text" value={diLocationLoading ? "Fetching location..." : diLocation} readOnly placeholder="Location auto-fills from pincode" className="w-full bg-gray-50 border border-gray-300 rounded-md px-3 py-2.5 text-sm text-[#111827]" />
                      </div>
                    </div>
                    <div className="mt-6 grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-6">
                      <div className="space-y-3">
                        {diLocation && diBrandOffers.length === 0 && <div className="border border-gray-200 rounded-lg p-4 text-sm text-[#4B5563]">No brand offers found for this pincode location yet.</div>}
                        {diBrandOffers.map((offer) => {
                          const isSelected = selectedDiBrand === offer.brand;
                          return (
                            <button key={offer.brand} type="button" onClick={() => setSelectedDiBrand(offer.brand)} className={`w-full text-left border rounded-lg p-4 ${isSelected ? "border-blue-600 bg-blue-50/30" : "border-gray-200 bg-white"}`}>
                              <div className="flex items-start justify-between gap-3">
                                <div className="font-bold text-lg text-[#111827]">{offer.brand}</div>
                                <div className="text-right">
                                  <div className="font-bold text-xl text-[#111827]">₹{offer.subTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div>
                                  <div className="text-xs text-[#6B7280]">{isSelected ? "Selected" : "Select"}</div>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      <div className="border border-gray-200 rounded-lg p-4 h-fit">
                        <h3 className="text-xl font-bold text-[#111827]">Best Price Estimator</h3>
                        {!selectedDiOffer ? <p className="text-sm text-[#6B7280] mt-3">Select a brand to view estimate details.</p> : (
                          <>
                            <div className="text-sm text-[#4B5563] mt-3">Brand</div>
                            <div className="font-semibold text-[#111827]">{selectedDiOffer.brand}</div>
                            <div className="text-sm text-[#4B5563] mt-1">{diLocation || "N/A"}</div>
                            <div className="mt-4 space-y-2 text-sm">
                              {selectedDiOffer.lineItems.map((item) => (
                                <div key={item.size} className="flex justify-between gap-2">
                                  <span>{item.size} ({item.qty} m)</span>
                                  <span className="font-semibold">₹{item.lineTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                                </div>
                              ))}
                            </div>
                            <div className="mt-4 pt-3 border-t border-gray-200 flex justify-between items-center">
                              <span className="font-semibold text-[#111827]">Total</span>
                              <span className="text-xl font-bold text-[#111827]">₹{selectedDiOffer.subTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="mt-6 flex justify-start">
                      <button onClick={() => setDiContractorStep(1)} className="bg-white border border-gray-300 text-[#111827] rounded-md px-5 py-2.5 text-sm font-semibold hover:bg-gray-50">Back</button>
                    </div>
                  </>
                )}
              </div>
            )}

            {isContractorAccBlock && (
              <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-6">
                {accContractorStep === 1 ? (
                  <>
                    <h2 className="text-xl md:text-2xl font-bold text-[#111827]">Select Size</h2>
                    <p className="text-sm text-[#4B5563] mt-1 mb-5">Enter required meter cube (m3) for each size.</p>
                    <div className="space-y-3">
                      {accContractorRows.map((row) => {
                        const selected = Number(row.m3_input) > 0;
                        return (
                          <div key={row.id} className={`border rounded-lg p-3 md:p-4 ${selected ? "border-blue-500 bg-blue-50/30" : "border-gray-200"}`}>
                            <div className="grid grid-cols-[28px_minmax(0,1fr)_minmax(96px,120px)] sm:grid-cols-[28px_minmax(0,1fr)_150px] lg:grid-cols-[28px_1fr_180px] items-center gap-2 md:gap-3">
                              <div className={`w-7 h-7 rounded border text-sm flex items-center justify-center font-bold ${selected ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-gray-300 text-transparent"}`}>✓</div>
                              <div className="font-semibold text-[#111827]">{row.size}</div>
                              <input
                                {...decimalInputProps}
                                value={row.m3_input}
                                onChange={(e) => updateAccContractorRow(row.id, e.target.value)}
                                placeholder="Enter m3"
                                className="w-full min-w-0 bg-white border border-gray-300 rounded-md px-2 sm:px-3 py-2 text-sm gold-focus"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <>
                    <h2 className="text-xl md:text-2xl font-bold text-[#111827]">Delivery details</h2>
                    <p className="text-sm text-[#4B5563] mt-1 mb-5">Enter pincode to auto-fill location and show brand offers.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs uppercase tracking-wider font-semibold text-[#111827] mb-1.5">Pincode</label>
                        <input {...pincodeInputProps} value={accPincode} onChange={(e) => setAccPincode(digitsOnly(e.target.value, 6))} placeholder="Enter 6-digit pincode" className="w-full bg-white border border-gray-300 rounded-md px-3 py-2.5 text-sm gold-focus" />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-wider font-semibold text-[#111827] mb-1.5">Location</label>
                        <input type="text" value={accLocationLoading ? "Fetching location..." : accLocation} readOnly placeholder="Location auto-fills from pincode" className="w-full bg-gray-50 border border-gray-300 rounded-md px-3 py-2.5 text-sm text-[#111827]" />
                      </div>
                    </div>
                    <div className="mt-6 grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-6">
                      <div className="space-y-3">
                        {accLocation && accBrandOffers.length === 0 && <div className="border border-gray-200 rounded-lg p-4 text-sm text-[#4B5563]">No brand offers found for this pincode location yet.</div>}
                        {accBrandOffers.map((offer) => {
                          const isSelected = selectedAccBrand === offer.brand;
                          return (
                            <button key={offer.brand} type="button" onClick={() => setSelectedAccBrand(offer.brand)} className={`w-full text-left border rounded-lg p-4 ${isSelected ? "border-blue-600 bg-blue-50/30" : "border-gray-200 bg-white"}`}>
                              <div className="flex items-start justify-between gap-3">
                                <div className="font-bold text-lg text-[#111827]">{offer.brand}</div>
                                <div className="text-right">
                                  <div className="font-bold text-xl text-[#111827]">₹{offer.subTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div>
                                  <div className="text-xs text-[#6B7280]">{isSelected ? "Selected" : "Select"}</div>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      <div className="border border-gray-200 rounded-lg p-4 h-fit">
                        <h3 className="text-xl font-bold text-[#111827]">Best Price Estimator</h3>
                        {!selectedAccOffer ? <p className="text-sm text-[#6B7280] mt-3">Select a brand to view estimate details.</p> : (
                          <>
                            <div className="text-sm text-[#4B5563] mt-3">Brand</div>
                            <div className="font-semibold text-[#111827]">{selectedAccOffer.brand}</div>
                            <div className="text-sm text-[#4B5563] mt-1">{accLocation || "N/A"}</div>
                            <div className="mt-4 space-y-2 text-sm">
                              {selectedAccOffer.lineItems.map((item) => (
                                <div key={item.size} className="flex justify-between gap-2">
                                  <span>{item.size} ({item.qty} m3)</span>
                                  <span className="font-semibold">₹{item.lineTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                                </div>
                              ))}
                            </div>
                            <div className="mt-4 pt-3 border-t border-gray-200 flex justify-between items-center">
                              <span className="font-semibold text-[#111827]">Total</span>
                              <span className="text-xl font-bold text-[#111827]">₹{selectedAccOffer.subTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="mt-6 flex justify-start">
                      <button onClick={() => setAccContractorStep(1)} className="bg-white border border-gray-300 text-[#111827] rounded-md px-5 py-2.5 text-sm font-semibold hover:bg-gray-50">Back</button>
                    </div>
                  </>
                )}
              </div>
            )}

            {showBaseRowsTable && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="product-table-wrapper">
              {(slug === "cement" || isDiPipesPage) && (
                <div className="px-4 md:px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                  <span className="text-sm font-semibold text-[#111827]">Type</span>
                  <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
                    {(isDiPipesPage ? ["K7", "K9"] : ["OPC", "PPC"]).map((type) => (
                      <button
                        key={type}
                        onClick={() => (isDiPipesPage ? setDiPipeType(type) : setCementType(type))}
                        className={`px-4 py-2 text-sm font-semibold ${(isDiPipesPage ? diPipeType : cementType) === type ? "bg-[#C8A96A] text-white" : "bg-white text-[#111827]"}`}
                        data-testid={`${isDiPipesPage ? "di-type" : "cement-type"}-${type.toLowerCase()}`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {isVendorMaterialsPage && (
                <div className="border-b border-gray-100 bg-gray-50/60 px-4 md:px-6 py-4">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#111827]">Pincode</label>
                      <input
                        {...pincodeInputProps}
                        value={vendorDefaultPincode}
                        onChange={(e) => applyVendorDefaultPincode(e.target.value)}
                        placeholder="6-digit pincode"
                        className="w-full bg-white border border-gray-200 rounded-md px-3 py-2.5 text-sm gold-focus"
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#111827]">{vendorDefaultPriceLabel}</label>
                      <input
                        {...decimalInputProps}
                        value={vendorDefaultPrice}
                        onChange={(e) => applyVendorDefaultPrice(e.target.value)}
                        placeholder="Enter price"
                        className="w-full bg-white border border-gray-200 rounded-md px-3 py-2.5 text-sm gold-focus"
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-[#111827]">
                    <tr>
                      {slug === "cement" ? (
                        <>
                          <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">Brand name</th>
                          <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">Grade</th>
                          <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">Pincode</th>
                          <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">Price</th>
                          <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">
                            {isVendorCement ? "Stock" : "Quantity"}
                          </th>
                          <th className="text-right font-semibold uppercase tracking-wider text-xs py-4 px-4">Action</th>
                        </>
                      ) : isDiPipesPage ? (
                        <>
                          <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">Size</th>
                          <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">Brand</th>
                          <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">Pincode</th>
                          <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">{diPipeType} Price (per m)</th>
                          <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">{isVendorDiPipes ? "Stock" : "Quantity"}</th>
                          <th className="text-right font-semibold uppercase tracking-wider text-xs py-4 px-4">Action</th>
                        </>
                      ) : isAccBlockPage ? (
                        <>
                          <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">Size</th>
                          <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">Brand</th>
                          <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">Pincode</th>
                          <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">Price (per meter cube)</th>
                          <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">{isVendorAccBlock ? "Stock" : "Quantity"}</th>
                          <th className="text-right font-semibold uppercase tracking-wider text-xs py-4 px-4">Action</th>
                        </>
                      ) : (
                        <>
                          <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">Size</th>
                          <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">Brand</th>
                          <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">Pincode</th>
                          <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">Price</th>
                          <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">
                            {isVendorSteel ? "Stock" : "Quantity"}
                          </th>
                          <th className="text-right font-semibold uppercase tracking-wider text-xs py-4 px-4">Action</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => (
                      slug === "cement" ? (
                        <tr key={row.id} className={`border-t border-gray-100 hover:bg-[#FDFBF7]/70 transition-colors ${idx % 2 === 1 ? "bg-gray-50/40" : ""}`} data-testid={`product-row-${row.grade}`}>
                          <td className="py-4 px-4">
                            {isVendorCement ? (
                              <select
                                value={row.brand}
                                onChange={(e) => updateRow(row.id, { brand: e.target.value })}
                                disabled={Boolean(assignedVendorBrand)}
                                className="bg-white border border-gray-200 rounded-sm px-2 py-1.5 text-sm gold-focus"
                              >
                                {vendorAllowedBrands.map((b) => <option key={b}>{b}</option>)}
                              </select>
                            ) : (
                              <span className="font-semibold text-[#111827]">{row.brand}</span>
                            )}
                          </td>
                          <td className="py-4 px-4 font-semibold text-[#111827]">{row.grade}</td>
                          <td className="py-4 px-4">
                            {isVendorCement ? (
                              <input
                                {...pincodeInputProps}
                                value={row.location}
                                onChange={(e) => updateRow(row.id, { location: digitsOnly(e.target.value, 6) })}
                                placeholder="6-digit pincode"
                                className="w-32 bg-white border border-gray-200 rounded-sm px-2 py-1.5 text-sm gold-focus"
                              />
                            ) : (
                              <span className="font-semibold text-[#111827]">{row.location || "N/A"}</span>
                            )}
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-2">
                              {isVendorCement ? (
                                <input
                                  {...decimalInputProps}
                                  value={row.price_mt ?? row.price}
                                  onChange={(e) => {
                                    const value = decimalOnly(e.target.value, 2);
                                    updateRow(row.id, { price: value, price_mt: value });
                                  }}
                                  className="w-24 bg-white border border-gray-200 rounded-sm px-2 py-1.5 text-sm gold-focus font-semibold text-[#111827]"
                                />
                              ) : (
                                <span className="font-semibold text-[#111827]">₹{getDisplayPrice(slug, row).toLocaleString("en-IN")}</span>
                              )}
                              <select
                                value={row.unit}
                                onChange={(e) => {
                                  const unit = e.target.value;
                                  updateRow(row.id, { unit, quantity: CEMENT_QTY_OPTIONS[unit][0] });
                                }}
                                className="bg-white border border-gray-200 rounded-sm px-2 py-1 text-xs gold-focus"
                                data-testid={`unit-select-${row.grade}`}
                              >
                                <option value="bag">bag</option>
                                <option value="MT">MT</option>
                              </select>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            {isVendorCement ? (
                              <input
                                {...integerInputProps}
                                value={row.quantity}
                                onChange={(e) => updateRow(row.id, { quantity: integerOnly(e.target.value) })}
                                className="w-24 bg-white border border-gray-200 rounded-sm px-2 py-1.5 text-sm gold-focus"
                              />
                            ) : (
                              <select
                                value={row.quantity}
                                onChange={(e) => updateRow(row.id, { quantity: e.target.value })}
                                className="bg-white border border-gray-200 rounded-sm px-2 py-1.5 text-sm gold-focus"
                                data-testid={`qty-select-${row.grade}`}
                              >
                                {CEMENT_QTY_OPTIONS[row.unit].map((q) => (
                                  <option key={q} value={q}>{q}</option>
                                ))}
                              </select>
                            )}
                          </td>
                          <td className="py-4 px-4 text-right">
                            {isVendorCement ? (
                              <button
                                onClick={() => handleVendorAddProduct(row)}
                                disabled={!!addingByRow[row.id]}
                                className="btn-gold rounded-sm px-4 py-2 text-xs font-semibold disabled:opacity-60"
                              >
                                {addingByRow[row.id] ? "Adding..." : "Add Product"}
                              </button>
                            ) : (
                              <button
                                onClick={() => handleAdd(row)}
                                className="btn-gold rounded-sm px-4 py-2 text-xs font-semibold"
                                data-testid={`add-to-cart-${row.grade}`}
                              >
                                Add to Cart
                              </button>
                            )}
                          </td>
                        </tr>
                      ) : isDiPipesPage ? (
                        <tr key={row.id} className={`border-t border-gray-100 hover:bg-[#FDFBF7]/70 transition-colors ${idx % 2 === 1 ? "bg-gray-50/40" : ""}`} data-testid={`product-row-${row.size}`}>
                          <td className="py-4 px-4 font-semibold text-[#111827]">{row.size}</td>
                          <td className="py-4 px-4">
                            {isVendorDiPipes ? (
                              <select
                                value={row.brand}
                                onChange={(e) => updateRow(row.id, { brand: e.target.value })}
                                disabled={Boolean(assignedVendorBrand)}
                                className="bg-white border border-gray-200 rounded-sm px-2 py-1.5 text-sm gold-focus"
                              >
                                {vendorAllowedBrands.map((b) => <option key={b}>{b}</option>)}
                              </select>
                            ) : (
                              <span className="font-semibold text-[#111827]">{row.brand}</span>
                            )}
                          </td>
                          <td className="py-4 px-4">
                            <input
                              {...pincodeInputProps}
                              value={row.location}
                              onChange={(e) => updateRow(row.id, { location: digitsOnly(e.target.value, 6) })}
                              placeholder="6-digit pincode"
                              className="w-32 bg-white border border-gray-200 rounded-sm px-2 py-1.5 text-sm gold-focus"
                            />
                          </td>
                          <td className="py-4 px-4">
                            {isVendorDiPipes ? (
                              <input
                                {...decimalInputProps}
                                value={diPipeType === "K9" ? row.k9_price : row.k7_price}
                                onChange={(e) => {
                                  const value = decimalOnly(e.target.value, 2);
                                  updateRow(row.id, diPipeType === "K9" ? { k9_price: value } : { k7_price: value });
                                }}
                                className="w-28 bg-white border border-gray-200 rounded-sm px-2 py-1.5 text-sm gold-focus font-semibold text-[#111827]"
                              />
                            ) : (
                              <span className="font-semibold text-[#111827]">₹{getDisplayPrice(slug, row, diPipeType).toLocaleString("en-IN")}</span>
                            )}
                          </td>
                          <td className="py-4 px-4">
                            <input
                              {...integerInputProps}
                              value={row.quantity}
                              onChange={(e) => updateRow(row.id, { quantity: integerOnly(e.target.value) })}
                              className="w-20 bg-white border border-gray-200 rounded-sm px-2 py-1.5 text-sm gold-focus"
                            />
                          </td>
                          <td className="py-4 px-4 text-right">
                            {isVendorDiPipes ? (
                              <button
                                onClick={() => handleVendorAddProduct(row)}
                                disabled={!!addingByRow[row.id]}
                                className="btn-gold rounded-sm px-4 py-2 text-xs font-semibold disabled:opacity-60"
                              >
                                {addingByRow[row.id] ? "Adding..." : "Add Product"}
                              </button>
                            ) : (
                              <button
                                onClick={() => handleAdd(row)}
                                className="btn-gold rounded-sm px-4 py-2 text-xs font-semibold"
                              >
                                Add to Cart
                              </button>
                            )}
                          </td>
                        </tr>
                      ) : isAccBlockPage ? (
                        <tr key={row.id} className={`border-t border-gray-100 hover:bg-[#FDFBF7]/70 transition-colors ${idx % 2 === 1 ? "bg-gray-50/40" : ""}`} data-testid={`product-row-${row.size}`}>
                          <td className="py-4 px-4 font-semibold text-[#111827]">{row.size}</td>
                          <td className="py-4 px-4">
                            {isVendorAccBlock ? (
                              <select
                                value={row.brand}
                                onChange={(e) => updateRow(row.id, { brand: e.target.value })}
                                disabled={Boolean(assignedVendorBrand)}
                                className="bg-white border border-gray-200 rounded-sm px-2 py-1.5 text-sm gold-focus"
                              >
                                {vendorAllowedBrands.map((b) => <option key={b}>{b}</option>)}
                              </select>
                            ) : (
                              <span className="font-semibold text-[#111827]">{row.brand}</span>
                            )}
                          </td>
                          <td className="py-4 px-4">
                            <input
                              type="text"
                              {...pincodeInputProps}
                              value={row.location}
                              onChange={(e) => updateRow(row.id, { location: digitsOnly(e.target.value, 6) })}
                              placeholder="6-digit pincode"
                              className="w-32 bg-white border border-gray-200 rounded-sm px-2 py-1.5 text-sm gold-focus"
                            />
                          </td>
                          <td className="py-4 px-4">
                            {isVendorAccBlock ? (
                              <input
                                {...decimalInputProps}
                                value={row.price_per_m3}
                                onChange={(e) => updateRow(row.id, { price_per_m3: decimalOnly(e.target.value, 2) })}
                                className="w-28 bg-white border border-gray-200 rounded-sm px-2 py-1.5 text-sm gold-focus font-semibold text-[#111827]"
                              />
                            ) : (
                              <span className="font-semibold text-[#111827]">₹{getDisplayPrice(slug, row).toLocaleString("en-IN")}</span>
                            )}
                          </td>
                          <td className="py-4 px-4">
                            <input
                              {...integerInputProps}
                              value={row.quantity}
                              onChange={(e) => updateRow(row.id, { quantity: integerOnly(e.target.value) })}
                              className="w-20 bg-white border border-gray-200 rounded-sm px-2 py-1.5 text-sm gold-focus"
                            />
                          </td>
                          <td className="py-4 px-4 text-right">
                            {isVendorAccBlock ? (
                              <button
                                onClick={() => handleVendorAddProduct(row)}
                                disabled={!!addingByRow[row.id]}
                                className="btn-gold rounded-sm px-4 py-2 text-xs font-semibold disabled:opacity-60"
                              >
                                {addingByRow[row.id] ? "Adding..." : "Add Product"}
                              </button>
                            ) : (
                              <button
                                onClick={() => handleAdd(row)}
                                className="btn-gold rounded-sm px-4 py-2 text-xs font-semibold"
                              >
                                Add to Cart
                              </button>
                            )}
                          </td>
                        </tr>
                      ) : (
                        <tr key={row.id} className={`border-t border-gray-100 hover:bg-[#FDFBF7]/70 transition-colors ${idx % 2 === 1 ? "bg-gray-50/40" : ""}`} data-testid={`product-row-${row.size}`}>
                          <td className="py-4 px-4 font-semibold text-[#111827]">{row.size}</td>
                          <td className="py-4 px-4">
                            {isVendorSteel ? (
                              <select
                                value={row.brand}
                                onChange={(e) => updateRow(row.id, { brand: e.target.value })}
                                disabled={Boolean(assignedVendorBrand)}
                                className="bg-white border border-gray-200 rounded-sm px-2 py-1.5 text-sm gold-focus"
                                data-testid={`brand-select-${row.size}`}
                              >
                                {vendorAllowedBrands.map((b) => <option key={b}>{b}</option>)}
                              </select>
                            ) : (
                              <span className="font-semibold text-[#111827]">{row.brand}</span>
                            )}
                          </td>
                          <td className="py-4 px-4">
                            <input
                              {...pincodeInputProps}
                              value={row.location}
                              onChange={(e) => updateRow(row.id, { location: digitsOnly(e.target.value, 6) })}
                              placeholder="6-digit pincode"
                              className="w-32 bg-white border border-gray-200 rounded-sm px-2 py-1.5 text-sm gold-focus"
                              data-testid={`location-select-${row.size}`}
                            />
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-2">
                              {isVendorSteel ? (
                                <input
                                  {...decimalInputProps}
                                  value={row.price_mt ?? row.price}
                                  onChange={(e) => {
                                    const value = decimalOnly(e.target.value, 2);
                                    updateRow(row.id, { price: value, price_mt: value });
                                  }}
                                  className="w-24 bg-white border border-gray-200 rounded-sm px-2 py-1.5 text-sm gold-focus font-semibold text-[#111827]"
                                />
                              ) : (
                                <span className="font-semibold text-[#111827]">₹{getDisplayPrice(slug, row).toLocaleString("en-IN")}</span>
                              )}
                              <span className="text-xs text-[#4B5563]">per</span>
                              <select
                                value={row.unit}
                                onChange={(e) => updateRow(row.id, { unit: e.target.value })}
                                className="bg-white border border-gray-200 rounded-sm px-2 py-1 text-xs gold-focus"
                                data-testid={`unit-select-${row.size}`}
                              >
                                <option value="MT">MT</option>
                                {getSteelPiecesPerTonne(row.size) ? <option value="Piece">Piece</option> : null}
                              </select>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <input
                              {...integerInputProps}
                              value={row.quantity}
                              onChange={(e) => updateRow(row.id, { quantity: integerOnly(e.target.value) })}
                              className="w-20 bg-white border border-gray-200 rounded-sm px-2 py-1.5 text-sm gold-focus"
                              data-testid={`qty-input-${row.size}`}
                            />
                          </td>
                          <td className="py-4 px-4 text-right">
                            {isVendorSteel ? (
                              <button
                                onClick={() => handleVendorAddProduct(row)}
                                disabled={!!addingByRow[row.id]}
                                className="btn-gold rounded-sm px-4 py-2 text-xs font-semibold disabled:opacity-60"
                                data-testid={`add-product-${row.size}`}
                              >
                                {addingByRow[row.id] ? "Adding..." : "Add Product"}
                              </button>
                            ) : (
                              <button
                                onClick={() => handleAdd(row)}
                                className="btn-gold rounded-sm px-4 py-2 text-xs font-semibold"
                                data-testid={`add-to-cart-${row.size}`}
                              >
                                Add to Cart
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            )}

            {(slug === "steel-tmt" || slug === "cement" || isDiPipesPage || isAccBlockPage) && (isVendorMaterialsPage || isContractorMaterialsPage) && !isContractorSteel && !isContractorCement && !isContractorDiPipes && !isContractorAccBlock && (
              <div className="mt-8 bg-white border border-gray-200 rounded-xl overflow-hidden">
                {showVendorIdentityColumns && (
                  <div className="px-4 md:px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-[#111827]">
                      Products You Added
                    </h2>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-[#111827]">
                      <tr>
                        {showVendorIdentityColumns && (
                          <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">Vendor</th>
                        )}
                        {showVendorIdentityColumns && !(isContractorDiPipes || isContractorAccBlock) && (
                          <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">Vendor ID</th>
                        )}
                        <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">
                          {slug === "cement" ? "Type/Grade" : "Size"}
                        </th>
                        <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">Brand</th>
                        <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">Pincode</th>
                        <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">
                          {isContractorDiPipes ? `${diPipeType} Price (per m)` : (isContractorAccBlock ? "Price (per meter cube)" : "Price")}
                        </th>
                        <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">Quantity</th>
                        <th className="text-right font-semibold uppercase tracking-wider text-xs py-4 px-4">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(isContractorDiPipes
                        ? vendorVisibleRows.filter((r) => (r.unit || "K7") === diPipeType)
                        : vendorVisibleRows
                      ).length === 0 ? (
                        <tr>
                          <td className="py-6 px-4 text-[#4B5563]" colSpan={vendorProductsColSpan}>
                            {isVendorMaterialsPage ? "No products added yet." : "No vendor products available yet."}
                          </td>
                        </tr>
                      ) : (isContractorDiPipes
                        ? vendorVisibleRows.filter((r) => (r.unit || "K7") === diPipeType)
                        : vendorVisibleRows
                      ).map((row, idx) => (
                        <tr key={row.id} className={`border-t border-gray-100 ${idx % 2 === 1 ? "bg-gray-50/40" : ""}`}>
                          {showVendorIdentityColumns && (
                            <td className="py-4 px-4 text-[#111827]">{row.vendor_company || row.vendor_name || "Vendor"}</td>
                          )}
                          {showVendorIdentityColumns && !(isContractorDiPipes || isContractorAccBlock) && (
                            <td className="py-4 px-4 text-[#4B5563] text-xs">{row.vendor_code || row.vendor_id}</td>
                          )}
                          <td className="py-4 px-4 font-semibold text-[#111827]">{row.size}</td>
                          <td className="py-4 px-4">{row.brand}</td>
                          <td className="py-4 px-4">
                            {isVendorMaterialsPage ? (
                              <input
                                {...pincodeInputProps}
                                value={row.location}
                                onChange={(e) => updateVendorRowUi(row.id, { location: digitsOnly(e.target.value, 6) })}
                                className="w-32 bg-white border border-gray-200 rounded-sm px-2 py-1.5 text-sm gold-focus"
                              />
                            ) : (
                              row.location
                            )}
                          </td>
                          <td className="py-4 px-4 font-semibold text-[#111827]">
                            {isContractorMaterialsPage ? (
                              isContractorDiPipes ? (
                                <span>₹{Number(row.price).toLocaleString("en-IN")}</span>
                              ) : isContractorAccBlock ? (
                                <span>₹{Number(row.price).toLocaleString("en-IN")}</span>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <span>₹{getVendorRowDisplayPrice(row).toLocaleString("en-IN")}</span>
                                  <select
                                    value={row.selected_unit || row.unit}
                                    onChange={(e) => updateVendorRowUi(row.id, { selected_unit: e.target.value })}
                                    className="bg-white border border-gray-200 rounded-sm px-2 py-1 text-xs gold-focus"
                                  >
                                    {slug === "cement" ? (
                                      <>
                                        <option value="MT">MT</option>
                                        <option value="bag">bag</option>
                                      </>
                                    ) : (
                                      <>
                                        <option value="MT">MT</option>
                                        {getSteelPiecesPerTonne(row.size) ? <option value="Piece">Piece</option> : null}
                                      </>
                                    )}
                                  </select>
                                </div>
                              )
                            ) : isVendorMaterialsPage ? (
                              <input
                                {...decimalInputProps}
                                value={row.price}
                                onChange={(e) => updateVendorRowUi(row.id, { price: decimalOnly(e.target.value, 2) })}
                                className="w-28 bg-white border border-gray-200 rounded-sm px-2 py-1.5 text-sm gold-focus"
                              />
                            ) : (
                              isDiPipesPage ? (
                                <>₹{Number(row.price).toLocaleString("en-IN")} / m ({row.unit})</>
                              ) : isAccBlockPage ? (
                                <>₹{Number(row.price).toLocaleString("en-IN")} / m3</>
                              ) : (
                                <>₹{Number(row.price).toLocaleString("en-IN")} / {row.unit}</>
                              )
                            )}
                          </td>
                          <td className="py-4 px-4">
                            {isContractorMaterialsPage ? (
                              slug === "cement" ? (
                                <select
                                  value={row.selected_quantity || row.quantity}
                                  onChange={(e) => updateVendorRowUi(row.id, { selected_quantity: e.target.value })}
                                  className="bg-white border border-gray-200 rounded-sm px-2 py-1.5 text-sm gold-focus"
                                >
                                  {CEMENT_QTY_OPTIONS[row.selected_unit || row.unit || "MT"].map((q) => (
                                    <option key={q} value={q}>{q}</option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  {...decimalInputProps}
                                  value={row.selected_quantity || row.quantity}
                                  onChange={(e) => updateVendorRowUi(row.id, { selected_quantity: decimalOnly(e.target.value, 3) })}
                                  className="w-20 bg-white border border-gray-200 rounded-sm px-2 py-1.5 text-sm gold-focus"
                                />
                              )
                            ) : isVendorMaterialsPage ? (
                              <input
                                {...integerInputProps}
                                value={row.quantity}
                                onChange={(e) => updateVendorRowUi(row.id, { quantity: integerOnly(e.target.value) })}
                                className="w-20 bg-white border border-gray-200 rounded-sm px-2 py-1.5 text-sm gold-focus"
                              />
                            ) : (
                              row.quantity
                            )}
                          </td>
                          <td className="py-4 px-4 text-right">
                            {isContractorMaterialsPage ? (
                              <button
                                onClick={() => {
                                  addItem({
                                    product_slug: product.slug,
                                    product_name: product.name,
                                    brand: row.brand,
                                    size: isDiPipesPage ? `${row.size} - ${row.unit || diPipeType}` : row.size,
                                    location: row.location,
                                    unit: isDiPipesPage ? "m" : (isAccBlockPage ? "m3" : (row.selected_unit || row.unit)),
                                    price: (isDiPipesPage || isAccBlockPage) ? Number(row.price) : getVendorRowDisplayPrice(row),
                                    quantity: Number(row.selected_quantity || row.quantity),
                                    vendor_id: row.vendor_user_id || row.vendor_id || row.vendor_code || null,
                                    vendor_name: row.vendor_name || null,
                                    vendor_company: row.vendor_company || null,
                                  });
                                  toast.success(`${product.name} ${isDiPipesPage ? `${row.size} ${row.unit || diPipeType}` : row.size} added to cart`);
                                }}
                                className="btn-gold rounded-sm px-4 py-2 text-xs font-semibold"
                              >
                                Add to Cart
                              </button>
                            ) : isVendorMaterialsPage ? (
                              <button
                                onClick={() => saveVendorProductRow(row)}
                                disabled={!!savingVendorRowById[row.id]}
                                className="btn-gold rounded-sm px-4 py-2 text-xs font-semibold disabled:opacity-60"
                              >
                                {savingVendorRowById[row.id] ? "Saving..." : "Save"}
                              </button>
                            ) : (
                              <span className="text-xs text-[#4B5563]">Added</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="py-20 text-center text-[#4B5563]">Product not found</div>
        )}
      </section>
      <FloatingCartAction
        visible={!!floatingCartAction}
        disabled={floatingCartAction?.disabled}
        onClick={floatingCartAction?.onClick}
        testId={floatingCartAction?.testId}
      />
      <Footer />
    </div>
  );
}
